import React, {useEffect, useRef} from 'react';
import Animated, {
  FadeIn,
  SlideInUp,
  SlideOutDown,
  useSharedValue,
  ZoomIn,
} from 'react-native-reanimated';
import styled, {useTheme} from 'styled-components/native';
import {ViewStyle} from 'react-native';
import TextGeneric from '../../textGeneric';
import {tr} from '../../../translations';
import Icon from '../../icon';
import CircleRecordingView from '../../circleRecordingView';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';
import {useCustomHeaderHeight} from '../../../utils/hooks/useHeaderHeight';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {LinearGradient} from 'react-native-linear-gradient';

const Container = styled(Animated.View)<{
  screenWidth: number;
  screenHeight: number;
}>`
  width: ${({screenWidth}) => screenWidth}px;
  height: ${({screenHeight}) => screenHeight}px;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 98;
  background-color: ${({theme}) => theme.homeBackground};
`;
const BottomContainer = styled.View<{bottomInset: number; bottomWidth: number}>`
  align-items: center;
  justify-content: center;
  width: ${({bottomWidth}) => bottomWidth}px;
  padding-top: 5%;
  padding-bottom: ${({bottomInset}) => bottomInset}px;
`;

const QuestionText = styled(TextGeneric)`
  color: ${({theme}) => theme.text.dark};
  font-size: ${({theme}) => theme.text.s4}px;
  text-align: left;
  width: 100%;
`;
const ListeningText = styled(TextGeneric)`
  color: ${({theme}) => theme.text.dark};
  font-size: ${({theme}) => theme.text.s7}px;
  text-align: center;
  padding-bottom: ${({theme}) => theme.text.s4}px;
  width: 90%;
`;

const Row = styled.View`
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
  width: 80%;
  align-self: center;
`;
const IconContainer = styled.TouchableOpacity<{isPrimary?: boolean}>`
  width: ${({theme}) => theme.text.s1 * 1.3}px;
  height: ${({theme}) => theme.text.s1 * 1.3}px;
  background-color: ${({theme, isPrimary}) =>
    isPrimary ? theme.primary : theme.text.light};
  border-radius: 100px;
  align-items: center;
  justify-content: center;
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 2px 4px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  elevation: 1;
  margin-left: ${({theme}) => theme.text.s12}px;
  opacity: ${({disabled}) => (disabled ? 0.5 : 1)};
`;

const RecordingView = ({
  style,
  question,
  onClose,
  onSend,
  isRecording,
  contentContainerStyle,
}: {
  style?: ViewStyle;
  contentContainerStyle?: ViewStyle;
  question?: string;
  onClose?: () => void;
  onSend?: () => void;
  isRecording?: boolean;
}) => {
  const theme = useTheme();
  const {bottom: bottomInset} = useSafeAreaInsets();
  const headerHeight = useCustomHeaderHeight();
  const {hdp, wdp, getByScreenSize} = useResponsiveDimensions();
  const scrollViewRef = useRef<Animated.ScrollView>(null);
  const scrollY = useSharedValue(0);
  const contentHeight = useSharedValue(0);

  // Scroll to bottom when question updates
  useEffect(() => {
    if (question && scrollViewRef.current) {
      // Add a small delay to ensure the text has rendered
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({animated: true});
      }, 100);
    }
  }, [question]);

  const handleScroll = (event: any) => {
    scrollY.value = event.nativeEvent.contentOffset.y;
  };

  const handleContentSizeChange = (width: number, height: number) => {
    contentHeight.value = height;
  };

  const customEnteringAnimation = SlideInUp.springify()
    .damping(15)
    .mass(0.9)
    .stiffness(100);

  const customExitingAnimation = SlideOutDown.springify()
    .damping(15)
    .mass(0.9)
    .stiffness(100);

  if (!isRecording) {
    return <></>;
  }

  return (
    <Container
      style={style}
      screenWidth={wdp(100)}
      screenHeight={hdp(100)}
      entering={customEnteringAnimation}
      exiting={customExitingAnimation}>
      <LinearGradient
        style={[
          {
            flex: 1,
            width: '100%',
            paddingTop: headerHeight + theme.text.s2,
            alignItems: 'center',
            justifyContent: 'space-between',
          },
          contentContainerStyle,
        ]}
        colors={[
          theme.chat.startLinearGradient + '77',
          theme.chat.endLinearGradient + '77',
        ]}>
        <Animated.ScrollView
          ref={scrollViewRef}
          showsVerticalScrollIndicator={false}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          onContentSizeChange={handleContentSizeChange}
          style={{
            flexGrow: 1,
            width: '90%',
            alignSelf: 'center',
          }}
          contentContainerStyle={{
            flexGrow: 1,
            width: '100%',
            paddingVertical: 20,
          }}>
          <Animated.View entering={FadeIn.duration(300).delay(200)}>
            <QuestionText>{question || ''}</QuestionText>
          </Animated.View>
        </Animated.ScrollView>

        <BottomContainer
          bottomWidth={getByScreenSize('100%', '50%')}
          bottomInset={Math.max(bottomInset, hdp(2))}>
          <Animated.View
            style={{
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            entering={FadeIn.duration(400).delay(300)}>
            <ListeningText bold>{tr('home.listening')}</ListeningText>
          </Animated.View>
          <Row>
            <Animated.View entering={ZoomIn.duration(400).delay(400)}>
              <IconContainer onPress={onClose}>
                <Icon name={'x'} size={theme.text.s55} />
              </IconContainer>
            </Animated.View>
            <Animated.View
              style={{width: theme.text.s1 * 3.3}}
              entering={FadeIn.duration(400).delay(500)}>
              <CircleRecordingView isRecording />
            </Animated.View>
            <Animated.View entering={ZoomIn.duration(400).delay(400)}>
              <IconContainer
                disabled={question?.length === 0}
                isPrimary
                onPress={onSend}>
                <Icon
                  name={'keyboardArrowDown'}
                  style={{transform: [{rotate: '180deg'}]}}
                  size={theme.text.s55}
                />
              </IconContainer>
            </Animated.View>
          </Row>
        </BottomContainer>
      </LinearGradient>
    </Container>
  );
};

export default RecordingView;
